import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  Typography,
  Divider,
  Button,
} from '@material-ui/core';

import CloseButton from 'components/CustomButtons/CloseButton';
import { openModalPage, previousModalPage } from 'actions/modalPage';
import { SaveButton } from 'components/UIElements/UIButton';
import {
  saveTempSetting,
  commitTempSettings,
  dropTempSettings,
} from 'actions/configs';
import {
  getCustomPaymentTypes,
  getEnabledCustomPaymentsSetting,
} from 'reducers/PaymentTypes';
import { useAppDispatch } from 'reducers';
import { modalPages } from 'constants/modalPage';

interface CheckProps {
  name: string;
  type: string;
}

/**
 * Checkbox with label
 *
 * Includes a warning to the user if given a type that is not in a compatible format
 * Incompatible types are not correctly reported in Close day → Tenders and must be fixed in the BO
 */
const Check = React.memo<CheckProps>(({ name, type }) => {
  const { t } = useTranslation('settingsCustomPayment');
  const dispatch = useAppDispatch();

  const paymentTypes = useSelector(getCustomPaymentTypes);
  const enabledCustomPayments = useSelector(getEnabledCustomPaymentsSetting);

  const toggleCustomPaymentInPaymentView = (key, value) => {
    dispatch(
      saveTempSetting(
        'enabled_custom_payments',
        JSON.stringify(
          Object.fromEntries(
            paymentTypes
              .map(pt => pt.type)
              .filter(type =>
                type === key ? value : enabledCustomPayments[type],
              )
              .map(type => [type, 1]),
          ),
        ),
      ),
    );
  };

  const normalizedType = type
    .replace(/[ _]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
  return (
    <FormGroup>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <FormControlLabel
          label={name}
          labelPlacement="end"
          control={
            <Checkbox
              onChange={e =>
                toggleCustomPaymentInPaymentView(type, e.target.checked)
              }
              checked={enabledCustomPayments[type]}
            />
          }
        />
        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={normalizedType !== type}
          onClick={() =>
            dispatch(
              openModalPage({
                component: modalPages.CustomPaymentConfiguration,
                props: {
                  paymentType: type,
                },
              }),
            )
          }
        >
          {t('configure')}
        </Button>
      </Box>

      {normalizedType !== type && (
        <FormHelperText error={true}>
          <p>{t('invalidType')}</p>
          <p>
            {t('invalidTypeRecommendation', {
              current: type,
              recommended: normalizedType,
            })}
          </p>
        </FormHelperText>
      )}
    </FormGroup>
  );
});

function CustomPayment() {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('settingsCustomPayment');
  const paymentTypes = useSelector(getCustomPaymentTypes);

  const onClose = () => {
    dispatch(
      dropTempSettings([
        'enabled_custom_payments',
        'pos_custom_extra_card_payment_types',
      ]),
    );
    dispatch(previousModalPage());
  };

  const onSave = () => {
    dispatch(commitTempSettings(['enabled_custom_payments'])).then(onClose);
  };

  return (
    <div className="erply-settings">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="1rem"
      >
        <Box fontSize="1.75em" fontWeight={700}>
          {t('title')}
        </Box>
        <Box flexGrow={1} />
        <SaveButton action={onSave} variant="POS" />
        <CloseButton action={onClose} />
      </Box>
      <Divider />
      <Box padding="1rem">
        {paymentTypes?.length > 0 ? (
          paymentTypes?.map(paymentType => (
            <Check name={paymentType.name} type={paymentType.type} />
          ))
        ) : (
          <Typography variant="h6">{t('noCustomPayments')}</Typography>
        )}
      </Box>
    </div>
  );
}

export default CustomPayment;
